import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/ui/use-toast';

interface CouponOffer {
  id: string;
  business_id: string;
  header: string;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed_amount' | 'free_item';
  discount_value: number;
  minimum_purchase: number;
  valid_from: string;
  valid_until: string;
  usage_limit_per_customer: number;
  total_usage_limit: number;
  background_color: string;
  logo_url: string; // Deprecated - kept for backward compatibility
  hero_image_url: string; // Deprecated - kept for backward compatibility
  apple_logo_urls?: { '1x': string; '2x': string; '3x': string };
  google_logo_url?: string;
  hero_image_urls?: { '1x': string; '2x': string; '3x': string };
  is_active: boolean;
}

interface Business {
  name: string;
}

interface TokenInfo {
  remaining_uses: number;
  expires_at?: string;
  label?: string;
}

const CouponTokenRedemption: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [coupon, setCoupon] = useState<CouponOffer | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [googleWalletUrl, setGoogleWalletUrl] = useState<string | null>(null);
  const [generatingPass, setGeneratingPass] = useState(false);
  const [generatingApplePass, setGeneratingApplePass] = useState(false);
  const [applePassGenerated, setApplePassGenerated] = useState(false);
  const [autoAddingToWallet, setAutoAddingToWallet] = useState(false);

  // Device detection utility
  const detectDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid;
    
    return { isIOS, isAndroid, isMobile };
  };

  // Auto add to wallet once coupon is loaded and valid
  useEffect(() => {
    if (coupon && tokenInfo && isValid() && !autoAddingToWallet) {
      const { isIOS, isAndroid } = detectDevice();
      
      if (isIOS || isAndroid) {
        setAutoAddingToWallet(true);
        
        if (isAndroid) {
          // Auto-add to Google Wallet
          generateGoogleWalletPass();
        } else if (isIOS) {
          // Auto-add to Apple Wallet
          generateAppleWalletPass();
        }
      }
    }
  }, [coupon, tokenInfo, autoAddingToWallet]);

  useEffect(() => {
    const validateTokenAndFetchCoupon = async () => {
      if (!token) return;

      try {
        setLoading(true);
        
        // Supabase configuration
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        // Validate token first
        const tokenResponse = await fetch(`/functions/v1/validate-coupon-token/${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY,
          },
        });

        if (!tokenResponse.ok) {
          throw new Error('Invalid or expired token');
        }

        const tokenData = await tokenResponse.json();
        
        if (!tokenData.is_valid) {
          throw new Error(tokenData.error_message || 'Token is not valid');
        }

        // Set token info
        setTokenInfo(tokenData.token_info);

        // Now fetch the coupon offer
        const couponOffer = tokenData.coupon_offer;
        setCoupon(couponOffer);

        // Fetch business info
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('name')
          .eq('id', couponOffer.business_id)
          .single();

        if (businessError) throw businessError;
        setBusiness(businessData);

      } catch (error) {
        console.error('Error validating token:', error);
        setError(error instanceof Error ? error.message : 'Failed to load coupon');
      } finally {
        setLoading(false);
      }
    };

    validateTokenAndFetchCoupon();
  }, [token]);

  const getDiscountDisplay = () => {
    if (!coupon) return '';
    
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}% off`;
    } else if (coupon.discount_type === 'fixed_amount') {
      return `$${coupon.discount_value} off`;
    } else if (coupon.discount_type === 'free_item') {
      return 'Free item';
    }
    return 'Special offer';
  };

  const isExpired = () => {
    if (!coupon?.valid_until) return false;
    return new Date(coupon.valid_until) < new Date();
  };

  const isValidFrom = () => {
    if (!coupon?.valid_from) return true;
    return new Date(coupon.valid_from) <= new Date();
  };

  const isTokenExpired = () => {
    if (!tokenInfo?.expires_at) return false;
    return new Date(tokenInfo.expires_at) < new Date();
  };

  const isTokenExhausted = () => {
    return tokenInfo?.remaining_uses === 0;
  };

  const isValid = () => {
    return isValidFrom() && !isExpired() && !isTokenExpired() && !isTokenExhausted();
  };

  const consumeToken = async () => {
    if (!token) return;

    try {
      // Supabase configuration
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      const response = await fetch(`/functions/v1/consume-coupon-token/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          customer_id: 'token_customer_' + Date.now(),
          customer_name: 'Valued Customer'
        }),
      });

      if (!response.ok) {
        console.error('Failed to consume token');
        return;
      }

      const result = await response.json();
      console.log('✅ Token consumed, remaining uses:', result.remaining_uses);
      
      // Update token info
      setTokenInfo(prev => prev ? { ...prev, remaining_uses: result.remaining_uses } : null);
      
    } catch (error) {
      console.error('Error consuming token:', error);
    }
  };

  const generateGoogleWalletPass = async () => {
    if (!coupon) return;

    try {
      setGeneratingPass(true);

      console.log('🎫 Creating Google Wallet pass object for customer...');
      console.log('Coupon ID:', coupon.id);

      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'https://pass.taginn.app';

      // Create Google Wallet Offer Object (Class should already exist from dashboard)
      const response = await fetch(`${passGeneratorUrl}/google-wallet/create-offer-object`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coupon_offer_id: coupon.id,
          customer_data: {
            customerId: 'token_customer_' + Date.now(),
            customerName: 'Valued Customer'
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Google Wallet object creation failed:', response.status, errorText);
        throw new Error(`Failed to create Google Wallet offer: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Google Wallet offer object created:', result);
      
      if (result.saveUrl) {
        console.log('✅ Redirecting to Google Wallet:', result.saveUrl);
        toast.success('Redirecting to Google Wallet...');
        
        // Consume token before redirecting
        await consumeToken();
        
        // Auto-redirect to Google Wallet (same as /get-pass)
        window.location.href = result.saveUrl;
      } else {
        console.error('❌ No saveUrl in response:', result);
        throw new Error('Server did not return saveUrl');
      }
      
    } catch (error) {
      console.error('Error generating Google Wallet pass:', error);
      toast.error(`Failed to generate Google Wallet pass: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setGeneratingPass(false);
    }
  };

  const generateAppleWalletPass = async () => {
    if (!coupon) return;

    try {
      setGeneratingApplePass(true);

      console.log('🍎 Creating Apple Wallet coupon pass for customer...');
      console.log('Coupon ID:', coupon.id);

      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'https://pass.taginn.app';

      // Generate Apple Wallet coupon pass
      const response = await fetch(`${passGeneratorUrl}/apple-wallet/generate-coupon-pass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          couponData: {
            id: coupon.id,
            name: coupon.name,
            header: coupon.header,
            description: coupon.description,
            discount_type: coupon.discount_type,
            discount_value: coupon.discount_value,
            valid_until: coupon.valid_until,
            background_color: coupon.background_color,
            // New multi-resolution logo fields
            apple_logo_urls: coupon.apple_logo_urls,
            google_logo_url: coupon.google_logo_url,
            hero_image_urls: coupon.hero_image_urls,
            // Legacy fields for backward compatibility
            logo_url: coupon.logo_url,
            hero_image_url: coupon.hero_image_url,
            minimum_purchase: coupon.minimum_purchase
          },
          customerData: {
            customerId: 'token_customer_' + Date.now(),
            customerName: 'Valued Customer'
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Pass generation failed: ${response.statusText}`);
      }

      // Consume token before downloading
      await consumeToken();

      // Download the pass file
      const passBlob = await response.blob();
      const url = URL.createObjectURL(passBlob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${coupon.name.replace(/\s+/g, '-').toLowerCase()}-coupon.pkpass`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      setApplePassGenerated(true);
      toast.success('Apple Wallet coupon downloaded! It should open in Apple Wallet automatically.');
      
    } catch (error) {
      console.error('Error generating Apple Wallet pass:', error);
      toast.error(`Failed to generate Apple Wallet pass: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setGeneratingApplePass(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading coupon...</p>
        </div>
      </div>
    );
  }

  if (error || !coupon || !tokenInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="text-center py-8">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {isTokenExpired() ? 'Token Expired' : 
               isTokenExhausted() ? 'Token Exhausted' : 
               'Coupon Not Available'}
            </h1>
            <p className="text-gray-600">
              {error || 'This coupon link may have expired or is no longer available.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if this is a mobile device for auto-wallet functionality
  const { isIOS, isAndroid, isMobile } = detectDevice();

  // Show minimal loading screen for mobile devices during auto-add process
  if (isMobile && autoAddingToWallet && isValid()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <div className="mb-6">
            {coupon.logo_url && (
              <img 
                src={coupon.logo_url} 
                alt="Business logo" 
                className="w-16 h-16 rounded-full object-cover mx-auto mb-4"
              />
            )}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {coupon.header || business?.name || 'Special Offer'}
            </h1>
            <h2 className="text-xl text-blue-600 font-semibold mb-4">
              {coupon.name}
            </h2>
            <div className="text-lg font-bold text-green-600 mb-4">
              {getDiscountDisplay()}
            </div>
          </div>
          
          <div className="mb-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium">
              {isIOS ? 'Adding to Apple Wallet...' : 'Adding to Google Wallet...'}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Your coupon will be added automatically
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            {coupon.logo_url && (
              <img 
                src={coupon.logo_url} 
                alt="Business logo" 
                className="w-12 h-12 rounded-full object-cover"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {coupon.header || business?.name || 'Special Offer'}
              </h1>
              <p className="text-gray-600">Limited-Use Coupon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Hero Image */}
        {coupon.hero_image_url && (
          <div className="mb-8">
            <img 
              src={coupon.hero_image_url} 
              alt="Coupon hero" 
              className="w-full h-48 object-cover rounded-lg shadow-sm"
            />
          </div>
        )}

        {/* Token Info Banner */}
        {tokenInfo && tokenInfo.remaining_uses > 0 && (
          <div className="mb-6">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                  <p className="text-amber-800 font-medium text-sm">
                    Limited-Use Token • {tokenInfo.remaining_uses} {tokenInfo.remaining_uses === 1 ? 'use' : 'uses'} remaining
                    {tokenInfo.label && ` • ${tokenInfo.label}`}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Coupon Info Card */}
        <Card className="overflow-hidden shadow-lg border-0 bg-gradient-to-r from-blue-50 to-primary/10">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {coupon.name}
              </h2>
              <div className="text-2xl font-bold text-blue-600 mb-4">
                {getDiscountDisplay()}
              </div>
              <p className="text-gray-600">
                from {coupon.header || business?.name}
              </p>
            </div>

            {/* Validity Status */}
            <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
              {!isValid() ? (
                <div className="text-red-600">
                  {!isValidFrom() && (
                    <p className="font-semibold">Available from: {new Date(coupon.valid_from).toLocaleDateString()}</p>
                  )}
                  {isExpired() && (
                    <p className="font-semibold">❌ Expired on: {new Date(coupon.valid_until!).toLocaleDateString()}</p>
                  )}
                  {isTokenExpired() && (
                    <p className="font-semibold">❌ Token expired on: {new Date(tokenInfo.expires_at!).toLocaleDateString()}</p>
                  )}
                  {isTokenExhausted() && (
                    <p className="font-semibold">❌ No uses remaining</p>
                  )}
                </div>
              ) : (
                <div className="text-green-600">
                  <p className="font-semibold mb-1">✅ Valid Now</p>
                  <div className="text-gray-600 text-sm space-y-1">
                    {coupon.valid_until && (
                      <p>Valid until: {new Date(coupon.valid_until).toLocaleDateString()}</p>
                    )}
                    {tokenInfo.expires_at && (
                      <p>Token expires: {new Date(tokenInfo.expires_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Add to Wallet Section */}
            {isValid() && (
              <div className="space-y-4">
                <div className="border-t pt-6">
                  {!isMobile ? (
                    <>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Add to Your Wallet</h3>
                      <p className="text-gray-600 mb-6">
                        Save this offer to your digital wallet. The pass will contain a QR code for easy redemption at checkout.
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <p className="text-blue-800 text-sm">
                          💡 <strong>Pro tip:</strong> Open this link on your phone for automatic wallet addition!
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Manual Wallet Addition</h3>
                      <p className="text-gray-600 mb-6">
                        If the automatic addition didn't work, you can manually add to your wallet:
                      </p>
                    </>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Google Wallet Button */}
                    {!googleWalletUrl ? (
                      <Button 
                        onClick={generateGoogleWalletPass}
                        disabled={generatingPass}
                        size="lg"
                        className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg flex-1"
                      >
                        {generatingPass ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Add to Google Wallet
                          </>
                        )}
                      </Button>
                    ) : (
                      <a 
                        href={googleWalletUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg w-full">
                          <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Open Google Wallet
                        </Button>
                      </a>
                    )}

                    {/* Apple Wallet Button */}
                    <Button 
                      onClick={generateAppleWalletPass}
                      disabled={generatingApplePass}
                      size="lg"
                      className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 text-lg flex-1"
                    >
                      {generatingApplePass ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          Add to Apple Wallet
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Success Messages */}
                  {(googleWalletUrl || applePassGenerated) && (
                    <div className="mt-4 space-y-2">
                      {googleWalletUrl && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-blue-800 font-medium text-sm">✅ Google Wallet pass ready!</p>
                        </div>
                      )}
                      {applePassGenerated && (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-gray-800 font-medium text-sm">✅ Apple Wallet coupon downloaded!</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Offer Details */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-left space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Discount:</span>
                  <span className="text-gray-900">{getDiscountDisplay()}</span>
                </div>
                {coupon.minimum_purchase > 0 && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Minimum Purchase:</span>
                    <span className="text-gray-900">${coupon.minimum_purchase}</span>
                  </div>
                )}
                {coupon.usage_limit_per_customer > 1 && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Usage Limit:</span>
                    <span className="text-gray-900">{coupon.usage_limit_per_customer} times per customer</span>
                  </div>
                )}
                {tokenInfo && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Token Uses Remaining:</span>
                    <span className="text-gray-900">{tokenInfo.remaining_uses}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {!isValid() && (
          <div className="mt-8 text-center">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6">
                <p className="text-red-800 font-medium">
                  This offer is not currently available
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponTokenRedemption;